import { Link } from "@tanstack/react-router";
import { Fragment } from "react";
import type { Audience, FileNode } from "../../api.ts";
import { formatCount } from "../../lib/format.ts";
import { Icon } from "../Icon.tsx";
import styles from "./AudienceSummary.module.css";

type Effective = Audience["effective"];

export const audienceLabel = (effective: Effective): string =>
  effective.public
    ? "Anyone with the link"
    : effective.emails.length === 0
      ? "Only you"
      : formatCount(effective.emails.length, "person", "people");

export const audienceTone = (effective: Effective): "public" | "shared" | "private" =>
  effective.public ? "public" : effective.emails.length > 0 ? "shared" : "private";

export function AudienceSummary({ node, audience }: { node: FileNode; audience: Audience }) {
  const { effective, restrictedBy, mode } = audience;
  const ancestors = restrictedBy.filter((folder) => folder.id !== node.id);
  const tone = audienceTone(effective);

  return (
    <div className={styles.resolved} data-tone={tone}>
      <span className={styles.mark} aria-hidden="true">
        <Icon
          name={tone === "public" ? "globe" : tone === "shared" ? "people" : "lock"}
          className={styles.glyph}
        />
      </span>
      <div className={styles.text}>
        <p className={styles.title}>{audienceLabel(effective)}</p>
        {tone === "shared" ? <p className={styles.names}>{effective.emails.join(", ")}</p> : null}
        {ancestors.length > 0 ? (
          <p className={styles.source}>
            {mode === "inherit" ? "Inherits from " : "Also limited by "}
            {ancestors.map((folder, index) => (
              <Fragment key={folder.id}>
                {index > 0 ? ", " : ""}
                <Link to="/folders/$id" params={{ id: folder.id }} className={styles.link}>
                  {folder.name}
                </Link>
              </Fragment>
            ))}
          </p>
        ) : null}
      </div>
    </div>
  );
}
